// src/pages/outsourcing_management/EnquiryComposeDialog.tsx
//
// Build a Draft Inquiry from requisition lines, or change the lines of one
// that is still a Draft (EE-253 P2b). Top: the requisition lines as the
// requesters wrote them. Bottom: the Inquiry lines the supplier will be asked
// for, as the server would build them: raw material one line per stock, in
// stock lengths (P3); everything else one line per requisition line.
import { FunctionComponent, useContext, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import Close from "@mui/icons-material/Close";
import AppStateContext from "contexts/AppStateContext";
import { SupplierAutocomplete } from "components/Dashboard/supplierOption";
import { useComposeEnquiry, useSetEnquiryLines } from "hooks/useRequisitionPool";
import {
  apiPreviewEnquiryLines,
  AwaitingLineType,
  ComposedItemType,
} from "util/network/RequisitionPool";
import { InquiryDetailType } from "types/InquiryTypes";
import { reformatDate } from "util/DateUtil";

type PropsType = {
  open: boolean;
  // Set when changing the lines of an existing Draft Inquiry.
  inquiryId?: number | null;
  lines: AwaitingLineType[];
  partnerId?: number | null;
  deadline?: string | null;
  onClose: (savedId?: number) => void;
};

/** A Draft Inquiry's current requisition lines, in the pool's shape, so the
 * dialog can show them beside lines being added. `like` lends the type. */
export const poolLinesOf = (
  inquiry: InquiryDetailType,
  like?: Pick<AwaitingLineType, "type_code" | "type_label" | "inquiry_type">
): AwaitingLineType[] =>
  inquiry.items.flatMap((item) =>
    (item.sources ?? [])
      .filter((src) => src.live)
      .map(
        (src): AwaitingLineType => ({
          key: src.key,
          requisition_item_id: src.requisitionItemId,
          requisition_id: src.requisitionId,
          requisition_no: src.requisitionNo,
          line_no: src.lineNo,
          type_code: like?.type_code ?? null,
          type_label: like?.type_label ?? null,
          inquiry_type: like?.inquiry_type ?? inquiry.type,
          sales_order_id: src.salesOrderId,
          sales_order_no: src.salesOrderNo,
          quote_id: null,
          part_name: item.product?.name ?? null,
          // Not every product type has a 3D model.
          model_id:
            item.product?.details && "modelId" in item.product.details
              ? item.product.details.modelId ?? null
              : null,
          what: item.comment ?? "-",
          spec: "",
          pcs: null,
          required: src.required,
          required_amount: src.requiredAmount,
          required_unit: src.unit,
          required_by_date: src.requiredBy,
          supplier_id: null,
          supplier_name: null,
          requester_name: null,
          can_reject: false,
          // Pool grouping only; the dialog does not group.
          stock_key: null,
          group_key: src.key,
          group_label: "",
          matching_drafts: [],
        })
      )
  );

const head = (cells: string[]) => (
  <TableHead>
    <TableRow>
      {cells.map((cell, i) => (
        <TableCell key={i} sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}>
          {cell}
        </TableCell>
      ))}
    </TableRow>
  </TableHead>
);

const label = (line: AwaitingLineType) => `${line.requisition_no} / L${line.line_no ?? "?"}`;

const EnquiryComposeDialog: FunctionComponent<PropsType> = ({
  open,
  inquiryId,
  lines: initial,
  partnerId: initialPartner,
  deadline: initialDeadline,
  onClose,
}) => {
  const { token } = useContext(AppStateContext);
  const composeMutation = useComposeEnquiry(token);
  const setLinesMutation = useSetEnquiryLines(token);
  const [lines, setLines] = useState<AwaitingLineType[]>([]);
  const [partnerId, setPartnerId] = useState<number | null>(null);
  const [deadline, setDeadline] = useState("");
  const [items, setItems] = useState<ComposedItemType[]>([]);
  const [error, setError] = useState("");
  const editing = !!inquiryId;

  const earliest = useMemo(
    () =>
      lines
        .map((line) => line.required_by_date)
        .filter((d): d is string => !!d)
        .sort()[0] ?? "",
    [lines]
  );

  useEffect(() => {
    if (!open) return;
    setLines(initial);
    const suggested = initial.find((line) => line.supplier_id)?.supplier_id ?? null;
    setPartnerId(initialPartner ?? suggested);
    const dates = initial
      .map((line) => line.required_by_date)
      .filter((d): d is string => !!d)
      .sort();
    setDeadline(initialDeadline ?? dates[0] ?? "");
    setError("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // The server builds the Inquiry lines; ask it on every change.
  useEffect(() => {
    if (!open || !lines.length) {
      setItems([]);
      return;
    }
    let cancelled = false;
    apiPreviewEnquiryLines(token, {
      lines: lines.map((line) => line.requisition_item_id),
      inquiryId,
    })
      .then((preview) => {
        if (cancelled) return;
        setItems(preview.items);
        setError(preview.error ?? "");
      })
      .catch((err) => !cancelled && setError(String(err.message ?? err)));
    return () => {
      cancelled = true;
    };
  }, [open, lines, inquiryId, token]);

  const suggestedNames = Array.from(
    new Set(lines.map((line) => line.supplier_name).filter((name): name is string => !!name))
  );
  const late = lines.filter(
    (line) => deadline && line.required_by_date && line.required_by_date < deadline
  );
  const byId = new Map(lines.map((line) => [String(line.requisition_item_id), line]));
  const sourceLabel = (key: string) => {
    const line = byId.get(key);
    return line ? label(line) : key;
  };
  const saving = composeMutation.isLoading || setLinesMutation.isLoading;

  const save = () => {
    if (!lines.length) {
      setError("An Inquiry needs at least one line. Close the Inquiry instead.");
      return;
    }
    const ids = lines.map((line) => line.requisition_item_id);
    const done = {
      onSuccess: (inquiry: InquiryDetailType) => onClose(inquiry.id),
      onError: (err: any) => setError(String(err?.message ?? err)),
    };
    if (inquiryId) setLinesMutation.mutate({ inquiryId, lines: ids }, done);
    else composeMutation.mutate({ lines: ids, partnerId, deadline: deadline || null }, done);
  };

  return (
    <Dialog open={open} onClose={() => onClose()} maxWidth="lg" fullWidth>
      <DialogTitle>
        {editing
          ? `Edit requisition lines · INQ-${String(inquiryId).padStart(6, "0")}`
          : "New Inquiry from requisition lines"}
        <Typography variant="body2" color="text.secondary">
          {lines[0]?.type_label ? `${lines[0].type_label} · ` : ""}
          {lines.length} line(s) from {new Set(lines.map((line) => line.requisition_id)).size}{" "}
          requisition(s)
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {/* Supplier and deadline of an existing Draft are edited on the
            Inquiry itself; this only changes its lines. */}
        {!editing && (
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
            {/* Same service filter as the REQ form: partners registered for
                this type first, "Show all suppliers" for the rest. */}
            <SupplierAutocomplete
              sx={{ minWidth: 320 }}
              value={partnerId ?? undefined}
              onChange={(id) => setPartnerId(id ?? null)}
              serviceType={lines[0]?.inquiry_type ?? undefined}
              helperText={
                suggestedNames.length
                  ? `Suggested on the requisitions: ${suggestedNames.join(", ")}`
                  : "No supplier suggested"
              }
            />
            <TextField
              size="small"
              type="date"
              label="Deadline (one date on the PO)"
              InputLabelProps={{ shrink: true }}
              value={deadline}
              onChange={(e) => setDeadline(e.target.value)}
              helperText={earliest ? `Earliest required by: ${reformatDate(earliest)}` : " "}
            />
          </Box>
        )}
        {late.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }} data-testid="compose-late">
            {late.length} line(s) are required before this deadline:{" "}
            {late.map(label).join(", ")}
          </Alert>
        )}

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Requisition lines ({lines.length})
        </Typography>
        <Paper variant="outlined" sx={{ overflowX: "auto", mb: 3 }}>
          <Table size="small">
            {head(["Requisition / line", "SO", "Part", "Material / service", "Required", "Required by", ""])}
            <TableBody>
              {lines.map((line) => (
                <TableRow key={line.requisition_item_id} data-testid="compose-source">
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{label(line)}</TableCell>
                  <TableCell>{line.sales_order_no ?? "-"}</TableCell>
                  <TableCell>{line.part_name ?? "-"}</TableCell>
                  <TableCell>{line.what}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{line.required || "-"}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {line.required_by_date ? reformatDate(line.required_by_date) : "-"}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      title="Take out - back to Awaiting Inquiry"
                      onClick={() =>
                        setLines(
                          lines.filter((l) => l.requisition_item_id !== line.requisition_item_id)
                        )
                      }
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Inquiry lines ({items.length}) - what the supplier is asked for
        </Typography>
        <Paper variant="outlined" sx={{ overflowX: "auto" }}>
          <Table size="small">
            {head(["Description", "Quantity", "Required", "From"])}
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.key} data-testid="compose-item">
                  <TableCell>
                    {item.name ? (
                      <>
                        <Typography variant="body2">{item.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {item.comment}
                        </Typography>
                      </>
                    ) : (
                      item.comment
                    )}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }} data-testid="compose-item-quantity">
                    {item.quantity == null
                      ? "-"
                      : item.unit === "lengths"
                        ? `${item.quantity} × ${item.stockMm ?? "?"} mm lengths`
                        : item.quantity}
                    {item.shortMm != null && (
                      <Typography variant="caption" color="error" display="block">
                        {item.shortMm} mm short - change it on the Inquiry
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{item.required || "-"}</TableCell>
                  <TableCell>{item.sources.map(sourceLabel).join(", ")}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }} data-testid="compose-error">
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1, pl: 2 }}>
          Saved as a Draft Inquiry. Lines can be added or taken out until it is sent.
        </Typography>
        <Button onClick={() => onClose()}>Close</Button>
        <Button
          variant="contained"
          disabled={saving || !!error || !lines.length}
          onClick={save}
          data-testid="compose-save"
        >
          {editing ? "Save lines" : "Create Draft Inquiry"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EnquiryComposeDialog;
